import { Button, DatePicker, Input, Table, Tag, Segmented } from 'antd';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { BiExport } from 'react-icons/bi';
import { FaPen, FaPlus, FaSearch } from 'react-icons/fa';
import { FaBoxArchive, FaBoxOpen } from "react-icons/fa6";
import { FiFilter } from 'react-icons/fi';
import { HiOutlineRefresh } from 'react-icons/hi';
import { MdOutlineKeyboardArrowLeft } from 'react-icons/md';
import { FaEye } from 'react-icons/fa';

import '../../css/UserAdmin/Global.css';

import Footer from './Footer';
import Header from './NewHeader';
import SideNav from './SideNav';

dayjs.extend(utc);
dayjs.extend(isBetween);

const { RangePicker } = DatePicker;

const ManageEnrollmentPeriodPage = () => {
    const navigate = useNavigate();
    const [isSideNavOpen, setIsSideNavOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
    const [activeTable, setActiveTable] = useState('terms');

    // ... (existing state variables) ...
    const [termDataSource, setTermDataSource] = useState([]);
    const [yearDataSource, setYearDataSource] = useState([]);
    const [termLoading, setTermLoading] = useState(false);
    const [yearLoading, setYearLoading] = useState(false);
    const [termSearchTerm, setTermSearchTerm] = useState('');
    const [yearSearchTerm, setYearSearchTerm] = useState('');
    const [termTableFilters, setTermTableFilters] = useState({});
    const [yearTableFilters, setYearTableFilters] = useState({});
    const [termSorter, setTermSorter] = useState({});
    const [yearSorter, setYearSorter] = useState({});
    const [showYearArchived, setShowYearArchived] = useState(false);
    const [showTermArchived, setShowTermArchived] = useState(false);


    const handleBack = () => navigate('/admin/manage-applications');
    const handleCreateTerm = () => navigate('/admin/manage-terms/create');
    const handleCreateYear = () => navigate('/admin/manage-years/create');

    const toggleSideNav = useCallback(() => setIsSideNavOpen(prev => !prev), []);

    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const genericHandleExport = async (exportType, reportNameForLog) => {
        try {
            setTermLoading(exportType === 'academic-term'); // Show loading on the respective table
            setYearLoading(exportType === 'academic-year');

            const currentDate = new Date().toISOString().split('T')[0];
            const fileName = `${exportType}-report-${currentDate}.pdf`;

            const fullName = localStorage.getItem('fullName');
            const role = localStorage.getItem('role');
            const userID = localStorage.getItem('userID');

            if (!userID || !fullName || !role) {
                toast.error("User information not found in local storage. Cannot export.");
                console.error("Export failed: User information missing from localStorage.");
                return;
            }

            const exportUrl = `${process.env.REACT_APP_API_URL}/api/admin/export/${exportType}?userID=${encodeURIComponent(userID)}&fullName=${encodeURIComponent(fullName)}&role=${encodeURIComponent(role)}`;

            const response = await fetch(exportUrl, { method: 'GET' });

            if (!response.ok) {
                const errorText = await response.text();
                toast.error(`Export failed: ${response.status} - ${errorText || 'Server error'}`);
                console.error('Export request failed:', response.status, errorText);
                return;
            }

            const blob = await response.blob();
            if (blob.type !== "application/pdf") {
                toast.error("Export failed: The downloaded file is not a PDF.");
                console.error("Export failed: incorrect blob type", blob.type);
                return;
            }

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.success(`${reportNameForLog} report exported successfully as ${fileName}`);

            const logData = {
                userID: userID,
                accountName: fullName,
                role: role,
                action: 'Export',
                detail: `Exported ${reportNameForLog} report: ${fileName}`
            };

            const logResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/system-logs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(logData)
            });

            if (logResponse.ok) {
                const logResult = await logResponse.json();
                console.log('System log recorded:', logResult);
            } else {
                console.error('Failed to record system log:', logResponse.status, await logResponse.text());
            }

        } catch (error) {
            toast.error("An error occurred during the export process. Please try again.");
            console.error('Export process error:', error);
        } finally {
            setTermLoading(false);
            setYearLoading(false);
        }
    };

    const handleTermExport = () => genericHandleExport('academic-term', 'Academic Terms');
    const handleYearExport = () => genericHandleExport('academic-year', 'Academic Years');

    // Fetch Academic Terms
    const fetchTerms = async (search = '', showArchived = false) => {
        console.log('fetchTerms called with:', { search, showArchived });
        setTermLoading(true);

        try {
            const url = `${process.env.REACT_APP_API_URL}/api/admin/terms?includeArchived=${showArchived ? 'true' : 'false'}&includeInactive=true`;
            const response = await fetch(url);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Error response:', errorText);
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }
            const result = await response.json();
            let termsArray = result.data || result;

            if (!Array.isArray(termsArray)) {
                console.error('Expected array but got:', termsArray);
                setTermDataSource([]);
                return;
            }

            const archiveStatusFilteredData = termsArray.filter(item => {
                const itemIsArchived = item.isArchived || false;
                return itemIsArchived === showArchived;
            });

            const searchedData = archiveStatusFilteredData.filter(item => {
                const searchableFields = [
                    item.term || '',
                    item.status || '',
                ].join(' ').toLowerCase();
                if (search.trim() === '') return true;
                return searchableFields.includes(search.toLowerCase());
            });

            const formattedData = searchedData.map((item, index) => ({
                key: item._id || index,
                term: item.term || 'N/A',
                academicYear: item.academicYear || 'N/A',
                status: item.status || 'N/A',
                startDate: item.startDate || null,
                endDate: item.endDate || null,
                enrollmentStartDate: item.enrollmentStartDate || null,
                enrollmentEndDate: item.enrollmentEndDate || null,
                createdAt: item.createdAt || null,
                updatedAt: item.updatedAt || null,
                isArchived: item.isArchived || false,
                examInterviewDates: item.examInterviewDates || []
            }));
            setTermDataSource(formattedData);
        } catch (error) {
            console.error('Error fetching terms:', error);
            setTermDataSource([]);
        } finally {
            setTermLoading(false);
        }
    };

    // Fetch Academic Years
    const fetchYears = async (search = '', showArchived = false) => {
        setYearLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/api/admin/years?includeArchived=${showArchived ? 'true' : 'false'}&includeInactive=true`;
            const response = await fetch(url);
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }
            const result = await response.json();
            let yearsArray = result.data || result;

            if (!Array.isArray(yearsArray)) {
                console.error('Expected array but got:', yearsArray);
                setYearDataSource([]);
                return;
            }
            const archiveStatusFilteredData = yearsArray.filter(item => {
                const itemIsArchived = item.isArchived || false;
                return itemIsArchived === showArchived;
            });
            const searchedData = archiveStatusFilteredData.filter(item => {
                const searchableFields = [
                    item.year || item.Year || '',
                    item.status || '',
                ].join(' ').toLowerCase();
                if (search.trim() === '') return true;
                return searchableFields.includes(search.toLowerCase());
            });
            const formattedData = searchedData.map((item, index) => ({
                key: item._id || index,
                year: item.year || item.Year || 'N/A',
                status: item.status || 'N/A',
                createdAt: item.createdAt || null,
                updatedAt: item.updatedAt || null,
                isArchived: item.isArchived || false,
            }));
            setYearDataSource(formattedData);
        } catch (error) {
            console.error('Error fetching years:', error);
            setYearDataSource([]);
        } finally {
            setYearLoading(false);
        }
    };

    const logSystemAction = async (action, detail) => {
        try {
            const fullName = localStorage.getItem('fullName');
            const role = localStorage.getItem('role');
            const userID = localStorage.getItem('userID');

            if (!userID || !fullName || !role) {
                console.error("User information not found in local storage. Cannot log action.");
                return; // Or throw an error if logging is critical
            }

            const logData = {
                userID: userID,
                accountName: fullName,
                role: role,
                action: action,
                detail: detail,
            };

            const logResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/system-logs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(logData)
            });

            if (logResponse.ok) {
                const logResult = await logResponse.json().catch(() => ({})); // Graceful parse
                console.log('System log recorded:', logResult);
            } else {
                const errorText = await logResponse.text().catch(() => "Failed to parse log error response");
                console.error('Failed to record system log:', logResponse.status, errorText);
            }
        } catch (error) {
            console.error('Error during system logging:', error);
        }
    };

    // Archive/Unarchive Term
    const handleTermArchiveToggle = async (record) => {
        const actionType = record.isArchived ? 'Unarchive' : 'Archive';
        const termIdentifier = `${record.term || 'N/A'}`; // More descriptive

        if (!window.confirm(`Are you sure you want to ${actionType.toLowerCase()} this record?`)) {
            return;
        }

        setTermLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/terms/archive/${record.key}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ isArchived: !record.isArchived })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: "Failed to toggle archive status." }));
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            toast.success(`Academic Term "${termIdentifier}" ${actionType.toLowerCase()}d successfully.`);

            // Log the action
            const logDetail = `${actionType}d Academic Term: "${termIdentifier}" [ID: ${record.key}]`;
            await logSystemAction(actionType, logDetail);

            await fetchTerms(termSearchTerm, showTermArchived); // Refresh the list
        } catch (error) {
            toast.error(`Error ${actionType.toLowerCase()}ing term: ${error.message}`);
            console.error(`Error ${actionType.toLowerCase()}ing term:`, error);
        } finally {
            setTermLoading(false);
        }
    };

    // Archive/Unarchive Year
    const handleYearArchiveToggle = async (record) => {
        const actionType = record.isArchived ? 'Unarchive' : 'Archive';
        const yearIdentifier = record.year || 'N/A';

        if (!window.confirm(`Are you sure you want to ${actionType.toLowerCase()} this record?`)) {
            return;
        }

        setYearLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/years/archive/${record.key}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ isArchived: !record.isArchived })
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: "Failed to toggle archive status." }));
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }
            toast.success(`Academic Year "${yearIdentifier}" ${actionType.toLowerCase()}d successfully.`);

            // Log the action
            const logDetail = `${actionType}d Academic Year: "${yearIdentifier}" [ID: ${record.key}]`;
            await logSystemAction(actionType, logDetail);

            await fetchYears(yearSearchTerm, showYearArchived); // Refresh the list
        } catch (error) {
            toast.error(`Error ${actionType.toLowerCase()}ing year: ${error.message}`);
            console.error(`Error ${actionType.toLowerCase()}ing year:`, error);
        } finally {
            setYearLoading(false);
        }
    };

    const handleTermSearch = (value) => {
        setTermSearchTerm(value);
        fetchTerms(value, showTermArchived);
    };
    const handleYearSearch = (value) => {
        setYearSearchTerm(value);
        fetchYears(value, showYearArchived);
    };
    const handleTermRefresh = () => {
        fetchTerms(termSearchTerm, showTermArchived);
    };
    const handleYearRefresh = () => {
        fetchYears(yearSearchTerm, showYearArchived);
    };
    const handleTermClearFilters = () => {
        setTermTableFilters({});
        setTermSorter({});
        setTermSearchTerm('');
        fetchTerms('', showTermArchived);
    };
    const handleYearClearFilters = () => {
        setYearTableFilters({});
        setYearSorter({});
        setYearSearchTerm('');
        fetchYears('', showYearArchived);
    };


    useEffect(() => {
        // Initial fetch based on active table, or fetch both if preferred.
        // For simplicity, let's fetch based on the default active table.
        if (activeTable === 'terms') {
            fetchTerms(termSearchTerm, showTermArchived);
        } else {
            fetchYears(yearSearchTerm, showYearArchived);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Initial fetch on mount

    useEffect(() => {
        if (activeTable === 'terms') {
            fetchTerms(termSearchTerm, showTermArchived);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showTermArchived, activeTable]); // Re-fetch terms when showTermArchived or activeTable (if it becomes 'terms') changes

    useEffect(() => {
        if (activeTable === 'years') {
            fetchYears(yearSearchTerm, showYearArchived);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showYearArchived, activeTable]); // Re-fetch years when showYearArchived or activeTable (if it becomes 'years') changes


    // Helper function to format dates with N/A fallback
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = dayjs(dateString);
        return date.isValid() ? date.format('YYYY-MM-DD HH:mm:ss') : 'N/A';
    };

    // Reusable date range filter component
    const DateRangeFilter = ({ selectedKeys, setSelectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
            <RangePicker
                style={{ width: 250 }}
                value={selectedKeys[0] ? [dayjs(selectedKeys[0].split('|')[0]), dayjs(selectedKeys[0].split('|')[1])] : []}
                onChange={(dates) => {
                    setSelectedKeys(dates ? [`${dates[0].toISOString()}|${dates[1].toISOString()}`] : []);
                }}
            />
            <div style={{ marginTop: 8 }}>
                <Button type="primary" onClick={() => confirm()}>Filter</Button> {/* Ensure confirm is called */}
                <Button onClick={() => { if (clearFilters) clearFilters(); confirm(); }} style={{ marginLeft: 8 }}>Reset</Button> {/* Ensure confirm is called after reset */}
            </div>
        </div>
    );

    // Helper to check if record is in range
    const isInDateRange = (value, dateField) => {
        if (!value || !dateField) return false;
        const [start, end] = value.split('|');
        const recordDate = dayjs(dateField);
        return recordDate.isValid() && recordDate.isBetween(dayjs(start), dayjs(end).endOf('day'), null, '[]');
    };

    const termColumns = [
        {
            title: 'Academic Term',
            width: 100,
            dataIndex: 'term',
            key: 'term',
            sorter: (a, b) => (a.term || '').localeCompare(b.term || ''),
            sortOrder: termSorter.columnKey === 'term' ? termSorter.order : null,
        },
        {
            title: 'Academic Year',
            width: 150,
            dataIndex: 'academicYear',
            key: 'academicYear',
            sorter: (a, b) => (a.academicYear || '').localeCompare(b.academicYear || ''),
            sortOrder: termSorter.columnKey === 'academicYear' ? termSorter.order : null,
        },
        {
            title: 'Status',
            width: 100,
            dataIndex: 'status',
            key: 'status',
            filters: [{ text: 'Active', value: 'Active' }, { text: 'Inactive', value: 'Inactive' }],
            onFilter: (value, record) => record.status === value,
            filteredValue: termTableFilters.status || null,
            render: (status) => (<Tag color={status === 'Active' ? 'green' : status === 'Inactive' ? 'volcano' : 'default'}>{status || 'N/A'}</Tag>),
        },
        {
            title: 'Enrollment Start Date',
            width: 180,
            dataIndex: 'enrollmentStartDate',
            key: 'enrollmentStartDate',
            render: (text) => dayjs(text).isValid() ? dayjs(text).format('MMM DD, YYYY') : 'N/A',
            sorter: (a, b) => dayjs(a.startDate).unix() - dayjs(b.startDate).unix(),
            sortOrder: termSorter.columnKey === 'enrollmentStartDate' ? termSorter.order : null,
            filterDropdown: (props) => <DateRangeFilter {...props} />,
            onFilter: (value, record) => isInDateRange(value, record.enrollmentStartDate),
            filteredValue: termTableFilters.enrollmentStartDate || null,
        },
        {
            title: 'Enrollment End Date',
            width: 180,
            dataIndex: 'enrollmentEndDate',
            key: 'enrollmentEndDate',
            render: (text) => dayjs(text).isValid() ? dayjs(text).format('MMM DD, YYYY') : 'N/A',
            sorter: (a, b) => dayjs(a.endDate).unix() - dayjs(b.endDate).unix(),
            sortOrder: termSorter.columnKey === 'enrollmentEndDate' ? termSorter.order : null,
            filterDropdown: (props) => <DateRangeFilter {...props} />,
            onFilter: (value, record) => isInDateRange(value, record.enrollmentEndDate),
            filteredValue: termTableFilters.enrollmentEndDate || null,
        },
        {
            title: 'Exam Start Date',
            width: 180,
            dataIndex: 'examOverallStartDate',
            key: 'examOverallStartDate Date',
            render: (text) => dayjs(text).isValid() ? dayjs(text).format('MMM DD, YYYY') : 'N/A',
            sorter: (a, b) => dayjs(a.examOverallStartDate).unix() - dayjs(b.examOverallStartDate).unix(),
            sortOrder: termSorter.columnKey === 'examOverallStartDate' ? termSorter.order : null,
            filterDropdown: (props) => <DateRangeFilter {...props} />,
            onFilter: (value, record) => isInDateRange(value, record.examOverallStartDate),
            filteredValue: termTableFilters.examOverallStartDate || null,
        },
        {
            title: 'Exam End Date',
            width: 180,
            dataIndex: 'examOverallEndDate',
            key: 'examOverallEndDate',
            render: (text) => dayjs(text).isValid() ? dayjs(text).format('MMM DD, YYYY') : 'N/A',
            sorter: (a, b) => dayjs(a.examOverallEndDate).unix() - dayjs(b.examOverallEndDate).unix(),
            sortOrder: termSorter.columnKey === 'examOverallEndDate' ? termSorter.order : null,
            filterDropdown: (props) => <DateRangeFilter {...props} />,
            onFilter: (value, record) => isInDateRange(value, record.examOverallEndDate),
            filteredValue: termTableFilters.examOverallEndDate || null,
        },
        // {
        //     title: 'Exam Slots Status',
        //     width: 180, // Adjust as needed
        //     dataIndex: 'dailyExamSchedules',
        //     key: 'examSlotsStatus',
        //     align: 'center',
        //     render: (dailySchedules) => { // dailySchedules is record.dailyExamSchedules
        //         if (!dailySchedules || !Array.isArray(dailySchedules) || dailySchedules.length === 0) {
        //             return <Tag>No Schedules</Tag>;
        //         }

        //         let availableSlots = 0;
        //         let unavailableSlots = 0;
        //         let totalConfiguredSlots = 0;

        //         dailySchedules.forEach(schedule => {
        //             if (schedule.timeSlots && Array.isArray(schedule.timeSlots)) {
        //                 schedule.timeSlots.forEach(slot => {
        //                     totalConfiguredSlots++;
        //                     if (slot.status === 'Available') {
        //                         availableSlots++;
        //                     } else { // Any other status (e.g., 'Unavailable', 'Booked')
        //                         unavailableSlots++;
        //                     }
        //                 });
        //             }
        //         });

        //         if (totalConfiguredSlots === 0) {
        //             // This implies there are day entries in dailyExamSchedules (e.g., for weekends)
        //             // but none of them contain any actual time slot configurations.
        //             return <Tag>No Slots Configured</Tag>;
        //         }

        //         return (
        //             <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
        //                 <Tag color="green" style={{ minWidth: '130px', textAlign: 'center' }}>Available: {availableSlots}</Tag>
        //                 <Tag color="volcano" style={{ minWidth: '130px', textAlign: 'center' }}>Unavailable: {unavailableSlots}</Tag>
        //                 {/* You could also show total slots if needed:
        //             <Tag color="blue" style={{minWidth: '130px', textAlign: 'center'}}>Total Slots: {totalConfiguredSlots}</Tag> 
        //             */}
        //             </div>
        //         );
        //     },
        // },
        {
            title: 'Created At',
            width: 180,
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (text) => formatDate(text),
            sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
            sortOrder: termSorter.columnKey === 'createdAt' ? termSorter.order : null,
            filterDropdown: (props) => <DateRangeFilter {...props} />,
            onFilter: (value, record) => isInDateRange(value, record.createdAt),
            filteredValue: termTableFilters.createdAt || null,
        },
        {
            title: 'Updated At',
            width: 180,
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            render: (text) => formatDate(text),
            sorter: (a, b) => dayjs(a.updatedAt).unix() - dayjs(b.updatedAt).unix(),
            sortOrder: termSorter.columnKey === 'updatedAt' ? termSorter.order : null,
            filterDropdown: (props) => <DateRangeFilter {...props} />,
            onFilter: (value, record) => isInDateRange(value, record.updatedAt),
            filteredValue: termTableFilters.updatedAt || null,
        },
        {
            title: 'Action',
            key: 'action',
            fixed: 'right',
            width: 180, // Adjusted width
            render: (_, record) => (
                <div style={{ display: 'flex', justifyContent: 'column', flexDirection: 'column', gap: '8px' }}>
                    <Button
                        icon={record.isArchived ? <FaEye /> : <FaPen />}
                        onClick={() => navigate(record.isArchived ? `/admin/manage-terms/view/${record.key}` : `/admin/manage-terms/edit/${record.key}`)}
                        style={{ width: '150px', margin: '0 auto', display: 'flex', justifyContent: 'flex-start' }}
                    >
                        {record.isArchived ? 'View' : 'Edit'}
                    </Button>
                    <Button
                        icon={record.isArchived ? <FaBoxOpen /> : <FaBoxArchive />}
                        onClick={() => handleTermArchiveToggle(record)}
                        disabled={(!record.isArchived && record.status === 'Active')}
                        title={(!record.isArchived && record.status === 'Active') ? 'Cannot archive active terms' : ''}
                        style={{ width: '150px', margin: '0 auto', display: 'flex', justifyContent: 'flex-start' }}
                        danger={!record.isArchived}
                    >
                        {record.isArchived ? 'Unarchive' : 'Archive'}
                    </Button>
                </div>
            )
        }
    ];

    const yearColumns = [
        {
            title: 'Academic Year',
            width: 200,
            dataIndex: 'year',
            key: 'year',
            sorter: (a, b) => (a.year || '').localeCompare(b.year || ''),
            sortOrder: yearSorter.columnKey === 'year' ? yearSorter.order : null,
        },
        {
            title: 'Status',
            width: 100,
            dataIndex: 'status',
            key: 'status',
            filters: [{ text: 'Active', value: 'Active' }, { text: 'Inactive', value: 'Inactive' }],
            onFilter: (value, record) => record.status === value,
            filteredValue: yearTableFilters.status || null,
            render: (status) => (<Tag color={status === 'Active' ? 'green' : status === 'Inactive' ? 'volcano' : 'default'}>{status || 'N/A'}</Tag>),
        },
        {
            title: 'Created At',
            width: 180,
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (text) => formatDate(text),
            sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
            sortOrder: yearSorter.columnKey === 'createdAt' ? yearSorter.order : null,
            filterDropdown: (props) => <DateRangeFilter {...props} />,
            onFilter: (value, record) => isInDateRange(value, record.createdAt),
            filteredValue: yearTableFilters.createdAt || null,
        },
        {
            title: 'Updated At',
            width: 180,
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            render: (text) => formatDate(text),
            sorter: (a, b) => dayjs(a.updatedAt).unix() - dayjs(b.updatedAt).unix(),
            sortOrder: yearSorter.columnKey === 'updatedAt' ? yearSorter.order : null,
            filterDropdown: (props) => <DateRangeFilter {...props} />,
            onFilter: (value, record) => isInDateRange(value, record.updatedAt),
            filteredValue: yearTableFilters.updatedAt || null,
        },
        {
            title: 'Action',
            key: 'action',
            fixed: 'right',
            width: 180, // Adjusted width
            render: (_, record) => (
                <div style={{ display: 'flex', justifyContent: 'column', flexDirection: 'column', gap: '8px' }}>
                    <Button
                        icon={record.isArchived ? <FaEye /> : <FaPen />}
                        onClick={() => navigate(record.isArchived ? `/admin/manage-years/view/${record.key}` : `/admin/manage-years/edit/${record.key}`)}
                        style={{ width: '150px', margin: '0 auto', display: 'flex', justifyContent: 'flex-start' }}
                    >
                        {record.isArchived ? 'View' : 'Edit'}
                    </Button>
                    <Button
                        icon={record.isArchived ? <FaBoxOpen /> : <FaBoxArchive />}
                        onClick={() => handleYearArchiveToggle(record)}
                        disabled={(!record.isArchived && record.status === 'Active')}
                        title={(!record.isArchived && record.status === 'Active') ? 'Cannot archive active years' : ''}
                        style={{ width: '150px', margin: '0 auto', display: 'flex', justifyContent: 'flex-start' }}
                        danger={!record.isArchived}
                    >
                        {record.isArchived ? 'Unarchive' : 'Archive'}
                    </Button>
                </div>
            )
        }
    ];


    const getCurrentTableData = () => {
        if (activeTable === 'terms') {
            return {
                dataSource: termDataSource,
                loading: termLoading,
                columns: termColumns,
                searchTerm: termSearchTerm,
                showArchived: showTermArchived,
                handleSearch: handleTermSearch,
                handleRefresh: handleTermRefresh,
                handleClearFilters: handleTermClearFilters,
                setShowArchived: setShowTermArchived,
                handleCreate: handleCreateTerm,
                tableFilters: termTableFilters,
                setTableFilters: setTermTableFilters,
                sorter: termSorter,
                setSorter: setTermSorter,
                tableName: 'Academic Term',
                handleExport: handleTermExport,
            };
        } else { // 'years'
            return {
                dataSource: yearDataSource,
                loading: yearLoading,
                columns: yearColumns,
                searchTerm: yearSearchTerm,
                showArchived: showYearArchived,
                handleSearch: handleYearSearch,
                handleRefresh: handleYearRefresh,
                handleClearFilters: handleYearClearFilters,
                setShowArchived: setShowYearArchived,
                handleCreate: handleCreateYear,
                tableFilters: yearTableFilters,
                setTableFilters: setYearTableFilters,
                sorter: yearSorter,
                setSorter: setYearSorter,
                tableName: 'Academic Year',
                handleExport: handleYearExport,
            };
        }
    };

    const currentTable = getCurrentTableData();

    return (
        <div className={`admin-page-container ${isSideNavOpen ? 'admin-sidenav-open' : ''}`}>
            <Header onMenuClick={toggleSideNav} isSideNavOpen={isSideNavOpen} />
            {((activeTable === 'terms' && showTermArchived) || (activeTable === 'years' && showYearArchived)) && (
                <Tag className="archived-tag" color="volcano" style={{ display: 'block', textAlign: 'center' }}>
                    Viewing Archived <strong>{activeTable === 'terms' ? 'ACADEMIC TERMS' : 'ACADEMIC YEARS'}</strong>
                </Tag>
            )}
            <div className="admin-content-wrapper">
                <div className="admin-sidenav-container">
                    <SideNav isOpen={isSideNavOpen} onClose={toggleSideNav} isSmallScreen={isSmallScreen} />
                </div>
                <main className="admin-main-content-area">
                    <div className="page-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <p className="heading" style={{ margin: 0 }}>
                                Manage Academic Period
                            </p>
                        </div>
                        <Segmented
                            options={[
                                { label: 'Manage Terms', value: 'terms' },
                                { label: 'Manage Years', value: 'years' }
                            ]}
                            value={activeTable}
                            onChange={(value) => {
                                setActiveTable(value);
                                // Optionally clear search and filters when switching tables
                                if (value === 'terms') {
                                    // handleTermClearFilters(); // or just reset search term
                                    setTermSearchTerm('');
                                } else {
                                    // handleYearClearFilters();
                                    setYearSearchTerm('');
                                }
                            }}
                            size="large"
                            style={{ backgroundColor: '#f0f0f0', border: '1px solid #d9d9d9', borderRadius: '8px' }}
                        />
                    </div>

                    <div style={{ marginTop: '20px', border: '1px solid #d9d9d9', borderRadius: '8px', padding: '20px' }}>
                        <h2 style={{ margin: '0 0 20px 0', padding: '0', fontWeight: 'bold', fontSize: '1.5em' }}>
                            Manage {currentTable.tableName}
                        </h2>

                        <div className="table-functions" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                            <div className="left-tools" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                <Button icon={<FiFilter />} onClick={currentTable.handleClearFilters}>
                                    Clear Filters
                                </Button>
                                <Button icon={<HiOutlineRefresh />} onClick={currentTable.handleRefresh} loading={currentTable.loading}>
                                    Refresh
                                </Button>
                                <Button icon={<BiExport />} onClick={currentTable.handleExport} loading={currentTable.loading}> {/* Updated onClick */}
                                    Export
                                </Button>
                            </div>
                            <div className="right-tools" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                <Input
                                    placeholder={`Search ${currentTable.tableName}`}
                                    allowClear
                                    style={{ width: 250 }}
                                    value={currentTable.searchTerm}
                                    onChange={(e) => currentTable.handleSearch(e.target.value)}
                                    suffix={<FaSearch style={{ color: '#aaa' }} />}
                                />
                                <Button
                                    icon={currentTable.showArchived ? <FaBoxOpen /> : <FaBoxArchive />}
                                    onClick={() => currentTable.setShowArchived(!currentTable.showArchived)}
                                >
                                    {currentTable.showArchived ? 'Close Archive' : 'Show Archive'}
                                </Button>
                                <Button
                                    type="primary" // Changed to primary for better visibility
                                    className="create-btn"
                                    icon={<FaPlus />}
                                    onClick={currentTable.handleCreate}
                                >
                                    Create {currentTable.tableName.replace('Academic ', '')} {/* Shorter button text */}
                                </Button>
                            </div>
                        </div>

                        <Table
                            style={{ width: '100%' }}
                            columns={currentTable.columns}
                            dataSource={Array.isArray(currentTable.dataSource) ? currentTable.dataSource : []}
                            loading={currentTable.loading}
                            scroll={{ x: 'max-content' }} // Better for responsiveness
                            pagination={{
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                                pageSizeOptions: ['10', '20', '50', '100'],
                                defaultPageSize: 10,
                            }}
                            bordered
                            onChange={(pagination, filters, sorter) => {
                                currentTable.setTableFilters(filters);
                                currentTable.setSorter(sorter);
                            }}
                            rowKey="key"
                        />
                    </div>
                </main>
            </div>
            <Footer />
        </div>
    )
}

export default ManageEnrollmentPeriodPage;